import { CardFace, CardBack } from './Card.js';
import type { Card } from '@durakapp/engine';

interface HandProps {
  cards: Card[];
  selectedIndex?: number | null;
  onSelect?: (i: number) => void;
}

const OWN_CARD_W = 64;
const OWN_OVERLAP_FRACTION = 0.35; // 35% of each card visible-only area covered by neighbour
const OPP_CARD_W = 46;
const OPP_OVERLAP_FRACTION = 0.45;

const OWN_FAN_DEG = 12; // outermost cards rotated by ±FAN_DEG/2
const OPP_FAN_DEG = 10;

/** Bottom hand — own cards face-up, fanned in a graceful arc. */
export function HandOwn({ cards, selectedIndex, onSelect }: HandProps) {
  const total = cards.length || 1;
  const step = OWN_CARD_W * (1 - OWN_OVERLAP_FRACTION);
  return (
    <div className="relative flex h-32 w-full items-end justify-center">
      <div className="relative" style={{ width: step * (total - 1) + OWN_CARD_W, height: '100%' }}>
        {cards.map((card, i) => {
          const t = total === 1 ? 0 : i / (total - 1) - 0.5;
          const rotate = t * OWN_FAN_DEG;
          const lift = Math.abs(t) * 8; // outer cards lift slightly so the arc curves up at edges
          const left = i * step;
          return (
            <div
              key={`${card.suit}-${card.rank}-${i}`}
              className="absolute bottom-0"
              style={{
                left,
                transform: `translateY(-${lift}px) rotate(${rotate}deg)`,
                transformOrigin: 'bottom center',
                zIndex: 10 + i,
              }}
            >
              <CardFace
                card={card}
                size="md"
                selected={selectedIndex === i}
                onClick={() => onSelect?.(i)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface OppProps {
  count: number;
}

export function HandOpponent({ count }: OppProps) {
  const total = Math.max(1, count);
  const step = OPP_CARD_W * (1 - OPP_OVERLAP_FRACTION);
  return (
    <div className="relative flex h-20 w-full items-start justify-center">
      <div className="relative" style={{ width: step * (total - 1) + OPP_CARD_W, height: '100%' }}>
        {Array.from({ length: count }).map((_, i) => {
          const t = total === 1 ? 0 : i / (total - 1) - 0.5;
          const rotate = -t * OPP_FAN_DEG; // mirrored relative to own hand
          const drop = Math.abs(t) * 6; // outer cards drop a touch — reverse curve from the bottom hand
          const left = i * step;
          return (
            <div
              key={i}
              className="absolute top-0"
              style={{
                left,
                transform: `translateY(${drop}px) rotate(${rotate}deg)`,
                transformOrigin: 'top center',
                zIndex: 10 + i,
              }}
            >
              <CardBack size="sm" />
            </div>
          );
        })}
      </div>
    </div>
  );
}
